import { Link } from "react-router-dom";
import { ArrowRight, Landmark, Building2, FileSearch, Hammer, PiggyBank } from "lucide-react";
import { motion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Seo } from "@/lib/seo";
import { Reveal, SectionHeading, CTASection, CheckItem, FestgeldStatusBadge, stagger, fadeUp } from "@/components/shared/Shared";

const BEREICHE = [
  {
    icon: Landmark,
    title: "Eleven Family Office",
    text: "Internationale Vermögensstrukturen, Verwaltung und langfristige Begleitung für Unternehmer, Investoren und vermögende Privatpersonen.",
    punkte: ["Analyse der Vermögensstruktur", "Vermögensverwaltung über regulierte Strukturen", "Kostenfreie Strukturanalyse als Einstieg"],
    to: "/family-office",
    testid: "leistungen-card-family-office",
  },
  {
    icon: Building2,
    title: "Eleven Real Estate",
    text: "Geprüfte Bestandswohnungen als langfristige Kapitalanlage – von der Finanzierung bis zur laufenden Betreuung.",
    punkte: ["Sechs feste Ankaufskriterien", "Begleitung bis zum Notartermin", "Kostenfreier Finanzierungscheck"],
    to: "/real-estate",
    testid: "leistungen-card-real-estate",
  },
  {
    icon: FileSearch,
    title: "Eleven Maklerservice",
    text: "Bestehende Vorsorgeverträge analysieren, Kosten transparent machen und mögliche Optimierungswege prüfen.",
    punkte: ["Nettopolicen ohne einkalkulierte Provision", "Keine vorschnelle Kündigung", "Konzept i. d. R. in 48 Stunden"],
    to: "/maklerservice",
    testid: "leistungen-card-maklerservice",
  },
  {
    icon: Hammer,
    title: "Eleven Development",
    text: "Eigene Projektentwicklung, Sanierung und Objektbetreuung – Qualität und Termine bleiben im Haus.",
    punkte: ["Sanierung nach festen Standards", "Über 400 Wohnungen in Verwaltung", "Enge Verzahnung mit Real Estate"],
    to: "/development",
    testid: "leistungen-card-development",
  },
];

export default function Leistungen() {
  return (
    <div className="bg-white">
      <Seo
        title="Leistungsbereiche – II Eleven Gruppe"
        description="Family Office, Real Estate, Maklerservice und Development: Die vier Leistungsbereiche der II Eleven Gruppe im Überblick – vier Bereiche, eine Verantwortung."
      />

      <section className="bg-[#111318] noise-overlay accent-hairline" data-testid="leistungen-hero">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <motion.div variants={stagger} initial="hidden" animate="show" className="max-w-3xl">
            <motion.div variants={fadeUp} className="flex items-center gap-3">
              <span className="h-px w-8 bg-[#FF3C20]" />
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-white/60">Leistungsbereiche</p>
            </motion.div>
            <motion.h1 variants={fadeUp} className="mt-5 font-display text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.08] text-white" data-testid="leistungen-hero-headline">
              Vier Bereiche, eine Verantwortung.
            </motion.h1>
            <motion.p variants={fadeUp} className="mt-5 text-base md:text-lg text-white/60 leading-relaxed max-w-[62ch]">
              Struktur, Immobilien, Vorsorge und Entwicklung greifen bei II Eleven ineinander. Jeder Bereich steht für sich – und Sie haben immer einen festen Ansprechpartner.
            </motion.p>
          </motion.div>
        </div>
      </section>

      <section className="bg-white py-14 sm:py-20" data-testid="leistungen-grid">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {BEREICHE.map((b) => (
              <Reveal key={b.title} className="h-full">
                <Link to={b.to} className="group flex h-full flex-col rounded-[8px] border border-border bg-[#F6F7F8] p-8 card-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF3C20]" data-testid={b.testid}>
                  <div className="h-11 w-11 rounded-[6px] bg-[#111318] flex items-center justify-center">
                    <b.icon className="h-5 w-5 text-[#FF3C20]" />
                  </div>
                  <h2 className="mt-5 font-display text-xl font-semibold text-[#111318]">{b.title}</h2>
                  <p className="mt-3 text-sm text-[#3B4452] leading-relaxed">{b.text}</p>
                  <div className="mt-5 space-y-2.5 flex-1">
                    {b.punkte.map((p) => (
                      <CheckItem key={p}>{p}</CheckItem>
                    ))}
                  </div>
                  <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[#111318] group-hover:text-[#E8241B] transition-colors">
                    Zum Bereich <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>

          {/* Zusatz: Festgeld */}
          <Reveal className="mt-8">
            <div className="rounded-[8px] border border-border bg-white p-8 flex flex-col md:flex-row md:items-center gap-6" data-testid="leistungen-card-festgeld">
              <div className="h-11 w-11 rounded-[6px] bg-[#F1F2F4] border border-border flex items-center justify-center shrink-0">
                <PiggyBank className="h-5 w-5 text-[#E8241B]" />
              </div>
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-3">
                  <h2 className="font-display text-lg font-semibold text-[#111318]">Ausgewählte Festgeldanfragen</h2>
                  <FestgeldStatusBadge size="sm" />
                </div>
                <p className="mt-2 text-sm text-[#3B4452] leading-relaxed max-w-3xl">
                  Ergänzend prüfen wir situationsbezogen die Verfügbarkeit von Festgeldlösungen. Konditionen werden grundsätzlich nicht öffentlich genannt, sondern erst nach individueller Prüfung persönlich kommuniziert.
                </p>
              </div>
              <Link to="/leistungen/festgeld" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#111318] hover:text-[#E8241B] transition-colors shrink-0" data-testid="leistungen-festgeld-link">
                Mehr erfahren <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <CTASection
        title="Nicht sicher, welcher Bereich der richtige ist?"
        subtitle="Genau dafür ist das Erstgespräch da: Wir hören zu und zeigen Ihnen den passenden Einstieg – kostenfrei und unverbindlich."
        primaryLabel="Persönliches Erstgespräch vereinbaren"
        primaryTo="/anfrage/erstgespraech"
        secondaryLabel="Termin online buchen"
        secondaryTo="/termin"
      />
    </div>
  );
}
